import { useStopSkillQueue, type SkillSessionDto } from '../api/skills';
import { itemIconPath } from '../compositor/assetPaths';
import { formatEta } from '../lib/formatEta';
import { AnticipationRing } from './AnticipationRing';

/** Elapsed share of a skill session's wait, 0..1. Same input `AnticipationRing` already clamps.
 *  Exported for the Habitat's one-line `SkillsLine` (R2), whose mini ring must draw the same arc
 *  as this card's — one derivation, not two. */
export function sessionElapsedFraction(session: SkillSessionDto, now: Date): number {
  const start = Date.parse(session.startedAt);
  const end = Date.parse(session.completesAt);
  if (!(end > start)) return 1;
  return (now.getTime() - start) / (end - start);
}

/**
 * The card's coarse ETA: `~2 min` under an hour (the spec's own copy, §3.5), and `formatEta`'s
 * `~1h 5m` beyond — a skill session is 2–15 minutes, so the hour form is a fallback, not a path
 * anyone should see. Same C3 coarsening as `formatEta` (minutes at finest, floored at 1).
 */
function etaLabel(completesAt: string, now: Date): string {
  const minutes = Math.max(1, Math.round((Date.parse(completesAt) - now.getTime()) / 60_000));
  return minutes < 60 ? `~${minutes} min` : formatEta(completesAt, now);
}

export type SkillBusyCardProps = {
  session: SkillSessionDto;
  now?: Date;
  onCancel?: () => void;
  cancelPending?: boolean;
  /** Append `at <placeName>` — for a surface away from the pin/bench the session runs at. */
  showPlace?: boolean;
};

/**
 * The running skill session as a card (skill-craft UI polish, spec §3.5): the elapsed ring with the
 * OUTPUT item's icon inside it, the server's `busyLabel` ("Chopping Copperwood Log" / "Smelt Copper
 * Bar"), a second line, and Cancel. Gather, craft and the Habitat share it so the busy copy cannot
 * drift back to codes — every word here is a DTO field; the card derives nothing but the clock.
 *
 * Second line: `~2 min · then 3 Copperwood Log` (gather — `outputQty` + the item name the server
 * already resolved into `actionName`) / `~2 min` (process) / `· at The Gearworks Anvil` appended
 * under `showPlace`. Once `now >= completesAt` the line is `Finishing up…`, the ring is full, and
 * Cancel is GONE — the 15 s sweep may already have applied the row, and the server refuses to
 * cancel an applied session, so offering it would be offering a 409. The card itself stays until
 * the poll (`useCompanionSkills`) sees the row disappear: the server owns "done" (§3.6).
 */
export function SkillBusyCard({ session, now, onCancel, cancelPending, showPlace }: SkillBusyCardProps) {
  const when = now ?? new Date();
  const done = when.getTime() >= Date.parse(session.completesAt);
  const label = session.busyLabel || session.actionName || session.actionCode;
  const parts: string[] = [];
  if (!done) {
    parts.push(etaLabel(session.completesAt, when));
    if (session.kind === 'gather' && session.actionName) parts.push(`then ${session.outputQty} ${session.actionName}`);
    if (showPlace && session.placeName) parts.push(`at ${session.placeName}`);
  }
  return (
    <div className="skill-busy-card">
      <div className="skill-ring">
        <AnticipationRing fraction={done ? 1 : sessionElapsedFraction(session, when)} size="sm" />
        <span className="skill-ring-inner">
          <img src={itemIconPath(session.outputItem)} alt="" />
        </span>
      </div>
      <div className="skill-busy-copy">
        <p className="skill-busy-label">{label}</p>
        <p className="skill-busy-meta">{done ? 'Finishing up…' : parts.join(' · ')}</p>
        {(session.repeatTotal ?? 1) > 1 && <QueueLine session={session} />}
      </div>
      {onCancel && !done && (
        <button
          type="button"
          className="btn-quiet"
          disabled={cancelPending}
          onClick={onCancel}
        >
          Cancel
        </button>
      )}
    </div>
  );
}

/**
 * Reward engine E1: the queue line, only for a session in a queue of two or more. `Session n of m`
 * names the session now running (`n = repeatTotal - repeatsLeft`) — not the brief's shorthand
 * `n of m left`, which reads as a remaining count and a position at once. `Stop` stays through
 * `Finishing up…` (unlike Cancel): stopping then is exactly the sweep race `useStopSkillQueue`
 * recovers from. `Last one` once nothing more will be armed — whether stopped or run to the end.
 * Its own component so the stop mutation (and its QueryClient) exists only where a queue does.
 * The accessible name says what Stop does beside Cancel; any failure the hook doesn't already
 * recover from (the 404 race) says so, or the keeper would believe a still-running queue stopped.
 */
function QueueLine({ session }: { session: SkillSessionDto }) {
  const stop = useStopSkillQueue();
  const left = session.repeatsLeft ?? 0;
  if (left === 0) return <p className="skill-busy-queue">Last one</p>;
  return (
    <>
      <p className="skill-busy-queue">
        <span>{`Session ${(session.repeatTotal ?? 1) - left} of ${session.repeatTotal}`}</span>
        <span aria-hidden="true">·</span>
        <button type="button" className="skill-busy-stop" aria-label="Stop after this session"
          disabled={stop.isPending} onClick={() => stop.mutate(session)}>
          Stop
        </button>
      </p>
      {stop.isError && <p className="field-error" role="alert">Couldn&rsquo;t stop the queue. Try again.</p>}
    </>
  );
}
